import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { CATEGORIES } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";

export const Footer = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-primary text-primary-foreground mt-24" data-testid="footer">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <span className="font-serif font-bold text-3xl tracking-[0.12em]">M&amp;A Jewelry</span>
        </div>

        <div>
          <h4 className="overline text-gold mb-5">{t("Shop")}</h4>
          <ul className="space-y-3 text-sm text-primary-foreground/70">
            {CATEGORIES.map((c) => (
              <li key={c.slug}>
                <Link to={`/category/${c.slug}`} className="hover:text-primary-foreground transition-colors">{t(c.name)}</Link>
              </li>
            ))}
            <li><Link to="/collections" className="hover:text-primary-foreground transition-colors">{t("Collections")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="overline text-gold mb-5">{t("Maison")}</h4>
          <ul className="space-y-3 text-sm text-primary-foreground/70">
            <li><Link to="/about" className="hover:text-primary-foreground transition-colors">{t("Our Story")}</Link></li>
            <li><Link to="/contact" className="hover:text-primary-foreground transition-colors">{t("Contact & Concierge")}</Link></li>
            <li><Link to="/shop" className="hover:text-primary-foreground transition-colors">{t("All Jewelry")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="overline text-gold mb-5">{t("The M&A Letter")}</h4>
          <p className="text-sm text-primary-foreground/60 mb-4">
            {t("Private previews, new arrivals and appointments.")}
          </p>
          <form onSubmit={subscribe} className="flex items-center border-b border-primary-foreground/30 focus-within:border-gold transition-colors">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("Email address")}
              className="border-0 bg-transparent px-0 focus-visible:ring-0 text-primary-foreground placeholder:text-primary-foreground/40"
              data-testid="footer-newsletter-input"
            />
            <Button type="submit" variant="ghost" size="icon" disabled={loading} className="hover:bg-transparent hover:text-gold" data-testid="footer-newsletter-submit">
              <ArrowRight className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>

      <div className="border-t border-primary-foreground/10">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-primary-foreground/50">
          <span>© {new Date().getFullYear()} M&amp;A Jewelry. {t("All rights reserved.")}</span>
          <div className="flex gap-6">
            <a href="#" className="hover:text-primary-foreground/80 transition-colors">{t("Privacy")}</a>
            <a href="#" className="hover:text-primary-foreground/80 transition-colors">{t("Terms")}</a>
            <a href="#" className="hover:text-primary-foreground/80 transition-colors">{t("Shipping & Returns")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
